// IMP-206: sessionStorage 키 네임스페이스 + 버전 관리 헬퍼
//
// 키 네이밍 컨벤션: <feature>:<purpose>:v<n>
//   예) pcActivation:specCheckDismissed:v1
//
// readVersionedBoolean  — 현재 버전 키를 읽고, 없으면 legacyKeys 마이그레이션
// writeVersionedBoolean — 현재 버전 키에 boolean 기록 (true→setItem / false→removeItem)

/**
 * 버전 관리 boolean 읽기.
 *
 * 1. currentKey 값이 'true'이면 true 반환.
 * 2. 아니면 legacyKeys 를 순회하여 'true' 인 키 발견 시:
 *    - currentKey 에 'true' 기록 (마이그레이션)
 *    - 모든 legacyKeys 제거
 *    - true 반환
 * 3. 발견하지 못하면 잔존 legacyKeys 정리 후 false 반환.
 * 4. SSR/예외 환경에서도 throw 하지 않고 false 반환.
 *
 * 복수 legacyKeys 가 모두 'true' 인 경우(데이터 불일치) 첫 번째로 발견된 키 기준으로
 * 마이그레이션하며, 나머지 legacyKeys 는 값과 무관하게 모두 제거된다.
 *
 * @param {string}   currentKey  현재 버전 키
 * @param {string[]} legacyKeys  마이그레이션 대상 구 버전 키 목록 (기본값: [])
 * @returns {boolean}
 */
export const readVersionedBoolean = (currentKey, legacyKeys = []) => {
    try {
        if (typeof sessionStorage === 'undefined') return false;

        // 1. 현재 버전 키 확인
        if (sessionStorage.getItem(currentKey) === 'true') return true;

        // 2. 레거시 키 마이그레이션
        let migrated = false;
        for (const legacyKey of legacyKeys) {
            if (!migrated && sessionStorage.getItem(legacyKey) === 'true') {
                migrated = true;
            }
        }

        // 잔존 legacyKeys 모두 정리 (값에 무관하게)
        for (const legacyKey of legacyKeys) {
            sessionStorage.removeItem(legacyKey);
        }

        if (migrated) {
            sessionStorage.setItem(currentKey, 'true');
            return true;
        }

        return false;
    } catch (_e) {
        return false;
    }
};

/**
 * 버전 관리 boolean 쓰기.
 *
 * value=true  → sessionStorage.setItem(currentKey, 'true')
 * value=false → sessionStorage.removeItem(currentKey)
 * SSR/예외 환경에서도 throw 하지 않음.
 *
 * @param {string}  currentKey 현재 버전 키
 * @param {boolean} value      저장할 값
 */
export const writeVersionedBoolean = (currentKey, value) => {
    try {
        if (typeof sessionStorage === 'undefined') return;
        if (value) {
            sessionStorage.setItem(currentKey, 'true');
        } else {
            sessionStorage.removeItem(currentKey);
        }
    } catch (_e) {
        // sessionStorage 미가용 환경 — 무해하게 종료
    }
};

/**
 * 버전 관리 JSON 읽기.
 *
 * 1. currentKey 의 JSON.parse 결과 반환.
 * 2. 없으면 legacyKeys 를 순회하여 첫 번째 유효한 값 마이그레이션:
 *    - currentKey 에 저장, 모든 legacyKeys 제거, 마이그레이션 값 반환.
 * 3. 없거나 parse 실패 시 fallback 반환.
 * 4. SSR/예외 환경에서도 throw 하지 않고 fallback 반환.
 *
 * @param {string}   currentKey  현재 버전 키
 * @param {*}        fallback    읽기 실패 시 반환값 (기본값: null)
 * @param {string[]} legacyKeys  마이그레이션 대상 구 버전 키 목록 (기본값: [])
 * @returns {*}
 */
export const readVersionedJSON = (currentKey, fallback = null, legacyKeys = []) => {
    try {
        if (typeof sessionStorage === 'undefined') return fallback;
        const cur = sessionStorage.getItem(currentKey);
        if (cur !== null) {
            try { return JSON.parse(cur); } catch (_e) { return fallback; }
        }
        // legacy 마이그레이션
        let migratedVal = null, found = false;
        for (const lk of legacyKeys) {
            if (!found) {
                const lv = sessionStorage.getItem(lk);
                if (lv !== null) { try { migratedVal = JSON.parse(lv); found = true; } catch (_e) {} }
            }
        }
        for (const lk of legacyKeys) sessionStorage.removeItem(lk);
        if (found) {
            try { sessionStorage.setItem(currentKey, JSON.stringify(migratedVal)); } catch (_e) {}
            return migratedVal;
        }
        return fallback;
    } catch (_e) { return fallback; }
};

/**
 * 버전 관리 JSON 쓰기.
 *
 * value=null/undefined → removeItem(키 제거).
 * 그 외 → JSON.stringify 후 setItem.
 * SSR/예외 환경에서도 throw 하지 않음.
 *
 * @param {string} currentKey 현재 버전 키
 * @param {*}      value      저장할 값 (null/undefined 면 키 제거)
 */
export const writeVersionedJSON = (currentKey, value) => {
    try {
        if (typeof sessionStorage === 'undefined') return;
        if (value === null || value === undefined) { sessionStorage.removeItem(currentKey); return; }
        sessionStorage.setItem(currentKey, JSON.stringify(value));
    } catch (_e) { /* no-op */ }
};
